import { useState, useEffect, useCallback } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { Layout } from '../../components/Layout'
import { hasAnyRole } from '../../utils/permissions'
import { supabase } from '../../services/supabase'
import { Plus, Edit, Trash2, X, Save, LogIn, CheckSquare, Square, Trash } from 'lucide-react'

const AdminUsers = () => {
  const { userProfile, loading: authLoading, impersonateUser } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [schools, setSchools] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role_id: '',
    school_id: '',
  })
  const [selectedUsers, setSelectedUsers] = useState([])
  
  // Hiyerarşi kontrolü
  const isSuperAdmin = userProfile && hasAnyRole(userProfile, ['super_admin'])
  const isAdmin = userProfile && hasAnyRole(userProfile, ['admin']) && !isSuperAdmin

  const fetchData = useCallback(async () => {
    try {
      // Rolleri al
      const rolesRes = await supabase.from('roles').select('*').order('name')
      if (rolesRes.error) throw rolesRes.error
      
      // Okulları al
      const schoolsRes = await supabase.from('schools').select('*').order('okul_adi')
      if (schoolsRes.error) throw schoolsRes.error
      
      // Kullanıcıları al (hiyerarşiye göre filtrele)
      let usersQuery = supabase
        .from('users')
        .select(`
          *,
          role:roles(*),
          school:schools(*)
        `)
        .order('created_at', { ascending: false })
      
      // Admin sadece mudur ve ogretmen görsün
      if (isAdmin && !isSuperAdmin) {
        // Mudur ve ogretmen rollerinin ID'lerini bul
        const mudurRole = rolesRes.data?.find(r => r.name === 'mudur')
        const ogretmenRole = rolesRes.data?.find(r => r.name === 'ogretmen')
        const allowedRoleIds = [mudurRole?.id, ogretmenRole?.id].filter(Boolean)
        
        if (allowedRoleIds.length > 0) {
          usersQuery = usersQuery.in('role_id', allowedRoleIds)
        } else {
          // Roller bulunamadıysa boş array döndür
          setUsers([])
          setRoles(rolesRes.data || [])
          setSchools(schoolsRes.data || [])
          return
        }
      }
      // Super admin tüm kullanıcıları görür (filtre yok)
      
      const usersRes = await usersQuery
      if (usersRes.error) throw usersRes.error

      setUsers(usersRes.data || [])
      setRoles(rolesRes.data || [])
      setSchools(schoolsRes.data || [])
    } catch (error) {
      console.error('Veri yüklenemedi:', error)
      alert('Veri yüklenirken hata oluştu: ' + error.message)
    } finally {
      setLoading(false)
    }
  }, [isAdmin, isSuperAdmin])

  useEffect(() => {
    if (userProfile) {
      // Kısa bir gecikme ile fetch (sayfa yüklenirken hata almayı önlemek için)
      const timer = setTimeout(() => {
        fetchData()
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [userProfile, fetchData])

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    )
  }

  if (!userProfile || !hasAnyRole(userProfile, ['super_admin', 'admin'])) {
    return <Navigate to="/unauthorized" replace />
  }

  const handleCreate = () => {
    setEditingUser(null)
    setFormData({
      email: '',
      password: '',
      full_name: '',
      phone: '',
      role_id: '',
      school_id: '',
    })
    setShowModal(true)
  }

  const handleEdit = (user) => {
    setEditingUser(user)
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name || '',
      phone: user.phone || '',
      role_id: user.role_id || '',
      school_id: user.school_id || '',
    })
    setShowModal(true)
  }

  const handleImpersonate = async (userId) => {
    // Hiyerarşi kontrolü - hangi kullanıcıya giriş yapılacak?
    const targetUser = users.find(u => u.id === userId)
    if (!targetUser) {
      alert('Kullanıcı bulunamadı')
      return
    }
    
    const targetRole = targetUser.role?.name
    
    // Admin sadece mudur'a girebilir
    if (isAdmin && !isSuperAdmin) {
      if (targetRole !== 'mudur') {
        alert('Yönetici sadece müdür kullanıcılarına giriş yapabilir.')
        return
      }
    }
    // Super admin hepsine girebilir (kontrol yok)
    
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Bu kullanıcı olarak oturum açmak istediğinize emin misiniz?\n\nYönetici arayüzüne dönmek için header\'daki "Yönetici Arayüzüne Dön" butonunu kullanabilirsiniz.')) {
      return
    }

    try {
      console.log('Impersonate başlatılıyor, userId:', userId, 'targetRole:', targetRole)
      await impersonateUser(userId)
      console.log('Impersonate başarılı, localStorage:', localStorage.getItem('meb_auth_user'))
      
      // Dashboard'a yönlendir (reload yok, state zaten güncellendi)
      navigate('/')
    } catch (error) {
      console.error('Impersonate hatası:', error)
      alert('Oturum açma hatası: ' + (error.message || 'Bilinmeyen hata'))
    }
  }

  const handleDelete = async (userId) => {
    // Hiyerarşi kontrolü - hangi kullanıcı silinecek?
    const targetUser = users.find(u => u.id === userId)
    if (!targetUser) {
      alert('Kullanıcı bulunamadı')
      return
    }
    
    const targetRole = targetUser.role?.name
    
    // Admin sadece mudur ve ogretmen silebilir
    if (isAdmin && !isSuperAdmin) {
      if (targetRole !== 'mudur' && targetRole !== 'ogretmen') {
        alert('Yönetici sadece müdür ve öğretmen kullanıcılarını silebilir.')
        return
      }
    }
    // Super admin hepsini silebilir (kontrol yok)
    
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) return

    try {
      const { error } = await supabase.from('users').delete().eq('id', userId)
      if (error) throw error
      setSelectedUsers([])
      await fetchData()
    } catch (error) {
      alert('Silme işlemi başarısız: ' + error.message)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      alert('Lütfen silmek için en az bir kullanıcı seçin')
      return
    }

    // Hiyerarşi kontrolü - seçili kullanıcıları kontrol et
    const selectedUsersData = users.filter(u => selectedUsers.includes(u.id))
    if (isAdmin && !isSuperAdmin) {
      const invalidUsers = selectedUsersData.filter(u => {
        const role = u.role?.name
        return role !== 'mudur' && role !== 'ogretmen'
      })
      if (invalidUsers.length > 0) {
        alert('Yönetici sadece müdür ve öğretmen kullanıcılarını silebilir.')
        return
      }
    }

    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`${selectedUsers.length} kullanıcıyı silmek istediğinize emin misiniz?`)) return

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .in('id', selectedUsers)

      if (error) throw error
      setSelectedUsers([])
      await fetchData()
      alert(`${selectedUsers.length} kullanıcı başarıyla silindi`)
    } catch (error) {
      alert('Toplu silme işlemi başarısız: ' + error.message)
    }
  }

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([])
    } else {
      setSelectedUsers(users.map(u => u.id))
    }
  }

  const toggleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId))
    } else {
      setSelectedUsers([...selectedUsers, userId])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Hiyerarşi kontrolü - yeni kullanıcı eklerken
    if (!editingUser && formData.role_id) {
      const selectedRole = roles.find(r => r.id === formData.role_id)
      const selectedRoleName = selectedRole?.name
      
      // Admin sadece mudur ve ogretmen ekleyebilir
      if (isAdmin && !isSuperAdmin) {
        if (selectedRoleName !== 'mudur' && selectedRoleName !== 'ogretmen') {
          alert('Yönetici sadece müdür ve öğretmen ekleyebilir.')
          return
        }
      }
    }
    
    // Güncelleme sırasında da kontrol et
    if (editingUser && formData.role_id) {
      const selectedRole = roles.find(r => r.id === formData.role_id)
      const selectedRoleName = selectedRole?.name
      
      // Admin sadece mudur ve ogretmen'e rol değiştirebilir
      if (isAdmin && !isSuperAdmin) {
        if (selectedRoleName !== 'mudur' && selectedRoleName !== 'ogretmen') {
          alert('Yönetici sadece müdür ve öğretmen rolü atayabilir.')
          return
        }
      }
    }

    try {
      if (editingUser) {
        // Güncelleme
        const updateData = {
          full_name: formData.full_name,
          phone: formData.phone,
          role_id: formData.role_id || null,
          school_id: formData.school_id || null,
        }

        const { error } = await supabase
          .from('users')
          .update(updateData)
          .eq('id', editingUser.id)

        if (error) throw error
      } else {
        // Yeni kullanıcı oluşturma (Custom Auth)
        const newUserId = crypto.randomUUID()
        const { error: userError } = await supabase.from('users').insert({
          id: newUserId,
          email: formData.email,
          password: formData.password, // Plain text - production'da hash'lenmeli
          full_name: formData.full_name,
          phone: formData.phone,
          role_id: formData.role_id || null,
          school_id: formData.school_id || null,
        })

        if (userError) throw userError
      }

      setShowModal(false)
      await fetchData()
    } catch (error) {
      alert('İşlem başarısız: ' + error.message)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Yükleniyor...</div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Kullanıcı Yönetimi</h1>
          <p className="text-gray-600 mt-1">Sistem kullanıcılarını yönetin</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedUsers.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 shadow-md transition-all duration-200"
            >
              <Trash size={18} />
              <span>Seçili Olanları Sil ({selectedUsers.length})</span>
            </button>
          )}
          <button
            onClick={handleCreate}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transition-all duration-200"
          >
            <Plus size={20} />
            <span>Yeni Kullanıcı</span>
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center justify-center"
                  title={selectedUsers.length === users.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                >
                  {selectedUsers.length === users.length && users.length > 0 ? (
                    <CheckSquare size={20} className="text-blue-600" />
                  ) : (
                    <Square size={20} className="text-gray-400" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ad Soyad
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                E-posta
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Telefon
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rol
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Okul
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className={selectedUsers.includes(user.id) ? 'bg-blue-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => toggleSelectUser(user.id)}
                    className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.full_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.phone || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.role?.display_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.school?.okul_adi || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    {/* Impersonate butonu - hiyerarşiye göre göster */}
                    {(() => {
                      const userRole = user.role?.name
                      // Admin sadece mudur'a girebilir, super_admin hepsine
                      const canImpersonate = isSuperAdmin || 
                        (isAdmin && !isSuperAdmin && userRole === 'mudur')
                      
                      if (canImpersonate) {
                        return (
                          <button
                            onClick={() => handleImpersonate(user.id)}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 transition-colors"
                            title="Bu kullanıcı olarak oturum aç"
                          >
                            <LogIn size={18} />
                          </button>
                        )
                      }
                      return null
                    })()}
                    <button
                      onClick={() => handleEdit(user)}
                      className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                      title="Düzenle"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                      title="Sil"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-posta
                </label>
                <input
                  type="email"
                  required={!editingUser}
                  disabled={!!editingUser}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                />
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Şifre
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ad Soyad
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rol
                </label>
                <select
                  value={formData.role_id}
                  onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                  disabled={editingUser && isAdmin && !isSuperAdmin}
                >
                  <option value="">Rol Seçin</option>
                  {roles
                    .filter((role) => {
                      // Hiyerarşi kontrolü: Admin sadece mudur ve ogretmen ekleyebilir
                      if (isAdmin && !isSuperAdmin) {
                        return role.name === 'mudur' || role.name === 'ogretmen'
                      }
                      // Super admin tüm rolleri görebilir
                      return true
                    })
                    .map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.display_name}
                      </option>
                    ))}
                </select>
                {isAdmin && !isSuperAdmin && (
                  <p className="text-xs text-gray-500 mt-1">
                    Yönetici sadece müdür ve öğretmen ekleyebilir
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Okul
                </label>
                <select
                  value={formData.school_id}
                  onChange={(e) => setFormData({ ...formData, school_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                >
                  <option value="">Okul Seçin</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.okul_adi}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-md transition-all duration-200"
                >
                  <Save size={18} />
                  <span>Kaydet</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default AdminUsers

